import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["text", "pattern", "renderHighlight"];
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { escapeRegex } from '../../util/escapeRegex';
import { Highlight } from '../Highlight/Highlight';
import { StyledHighlighter } from './Highlighter.styles';

/**
 * A utility component that will break a text string into matching pieces. This component provides
 * no style on its own other than wrapping the matching pieces with the `renderHighlight` function.
 *
 * @since 8.28.0
 */
export var Highlighter = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var text = _ref.text,
    pattern = _ref.pattern,
    renderHighlight = _ref.renderHighlight,
    rest = _objectWithoutProperties(_ref, _excluded);
  var content = text;
  if (pattern) {
    content = [];
    var regex = pattern;
    if (typeof pattern === 'string') {
      regex = new RegExp(escapeRegex(pattern), 'ig');
    }

    // RegExp object are stateful when global or sticky flags are set. We want to use this
    // state to iterate over the matches, but since we do not necessarily own the object,
    // we want to preserve the state before setting it the beginning.
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/exec
    var originalIndex = regex.lastIndex;
    regex.lastIndex = 0;
    var i = 0;
    var match = regex.exec(text);
    while (match) {
      // Push the text leading up to the match
      content.push(text.substring(i, match.index));

      // Push the match itself
      content.push(renderHighlight({
        key: match.index,
        children: match[0]
      }));

      // Move the pointer to after the match
      i = match.index + match[0].length;

      // Find the next match, making sure it is not a repeated match (will happen when the
      // RegExp object is not global)
      var prevIndex = match.index;
      match = regex.exec(text);
      if (match && match.index === prevIndex) {
        match = null;
      }
    }

    // Push the trailing text
    content.push(text.substring(i));

    // Restore original index state
    regex.lastIndex = originalIndex;
  }
  return /*#__PURE__*/React.createElement(StyledHighlighter, _extends({
    ref: forwardedRef
  }, rest), content);
});
Highlighter.displayName = 'Highlighter';
Highlighter.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The text to render.
   */
  text: PropTypes.string,
  /**
   * The pattern to find in `text`. When a string is passed, all matching strings will be
   * highlighted (case insensitive).
   */
  pattern: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(RegExp)]),
  /**
   * A render function that will be called for all matching segments.
   *
   * By default it uses a `Highlight` component, which is a styled
   * [HTML Mark Text element (`<mark>`)](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/mark).
   * You can select this default component in your own styles using `${StyledHighlight}`.
   */
  renderHighlight: PropTypes.func
} : {};
Highlighter.defaultProps = {
  renderHighlight: function renderHighlight(props) {
    return /*#__PURE__*/React.createElement(Highlight, props);
  }
};